import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, firstValueFrom, lastValueFrom, map, startWith } from 'rxjs';
import { ImportSafeBoxProductSku, CampaignsProductAdminApiClient, CampaignsSafeBoxAdminApiClient, ConsumptionValidationToolEnum, FilterValidationToolOperatorEnum } from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { ButtonComponent, LoadingOverlayComponent } from 'wecheer-common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'safe-box-import',
    templateUrl: './safe-box-import.component.html',
    styleUrls: ['./safe-box-import.component.scss'],
    imports: [
        LoadingOverlayComponent,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatIconModule,
        ReactiveFormsModule,
        CommonModule,
        ButtonComponent
    ]
})
export class SafeBoxImportComponent implements OnInit {
    private dialogRef = inject<MatDialogRef<SafeBoxImportComponent, ImportSafeBoxProductSku | undefined>>(MatDialogRef);
    private safeBoxAdminApi = inject(CampaignsSafeBoxAdminApiClient);
    private campaignsProductAdminApi = inject(CampaignsProductAdminApiClient);
    private snackBar = inject(MatSnackBar);
    editModel = inject<ImportSafeBoxProductSku>(MAT_DIALOG_DATA);


    static config = { width: '600px' };

    form: FormGroup = new FormGroup({
        productSkuId: new FormControl('', Validators.required),
        numberOfCharacters: new FormControl({ value: '', disabled: true }),
        format: new FormControl('', Validators.required),
        fileToUpload: new FormControl(null, Validators.required)
    });

    skuIdControl = this.form.controls.productSkuId as FormControl;
    filteredOptions: Observable<any[]>;

    isLoading = false;
    allowedValidationTools: ConsumptionValidationToolEnum[] = [
        ConsumptionValidationToolEnum.SecretCode,
        ConsumptionValidationToolEnum.UniqueQR
    ];

    addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    productSKUList = [];

    isDragOver = false;

    async ngOnInit() {
        await this.getProductList();

        this.initSkuFilter();
    }

    private initSkuFilter() {
        this.filteredOptions = this.skuIdControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                let name: string;
                if (typeof value === 'string') {
                    name = value;
                    this.form.controls.productSkuId.setErrors({ unselected: true })
                } else {
                    name = value?.name
                }
                return name;
            }),
            map(name => name ? this._filter(name || '') : this.productSKUList.slice())
        );
    }

    displayFn(product: any): string {
        return product && product.name ? product.name : '';
    }

    private _filter(name: string): any[] {
        const filterValue = name.toLowerCase();

        return this.productSKUList.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    async onSubmit() {
        if (this.form.invalid) {
            return;
        }

        const model = new ImportSafeBoxProductSku({
            format: this.form.value.format,
            productSkuName: this.form.value.productSkuId.name,
            numberOfCharacters: this.form.value.numberOfCharacters,
            productSkuId: this.form.value.productSkuId.id
        });

        this.isLoading = false;
        this.snackBar.open('Importing of codes has been started', null, { duration: 3000 });
        this.dialogRef.close(undefined);

        try {
            let uploadKey = await this.uploadFile(this.form.value.fileToUpload, model);

            if (!uploadKey) {
                return;
            }
            model.uploadKey = uploadKey;
            model.fileName = this.form.value.fileToUpload.name;
            await lastValueFrom(this.safeBoxAdminApi.importSafeBoxProductSku(model));
        } catch (err: any) {
            await lastValueFrom(this.safeBoxAdminApi.sendMailError('Failed to import safeboxes.', model));
        }
    }

    async uploadFile(file, model) {
        try {
            let uploadKey = `${GUID.newGuid().replace(/-/g, '')}.${file.name.split('.').pop()}`; // preserve extension on s3 key
            let uploadUrl = await lastValueFrom(this.safeBoxAdminApi.getUploadUrlS3(uploadKey));
            const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl, file));
            if (uploadSuccess) {
                return uploadKey;
            } else {
                await lastValueFrom(this.safeBoxAdminApi.sendMailError('Failed to upload CSV.', model));
                return null;
            }
        } catch (err) {
            await lastValueFrom(this.safeBoxAdminApi.sendMailError('Failed to upload CSV.', model));
            return null;
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        this.isDragOver = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;

        if (files && files.length > 0) {
            this.form.patchValue({
                fileToUpload: <File>files[0]
            });
        }
    }

    onFormatKeyUpEvent(event: any) {
        const regx = /[a-zA-Z0-9]/;
        const regRes = regx.test(event.target.value);
        if (regRes) {
            event.preventDefault();
            event.target.value = event.target.value.replace(regx, '');
        }

        let formatValue = event.target.value;

        if (!formatValue) {
            this.form.controls['numberOfCharacters'].setValue(0);
            return;
        }

        if (formatValue.indexOf('*') < 0) {
            this.form.controls['numberOfCharacters'].setValue(0);
            return;
        }

        let numberOfCharacters = formatValue.split('*').length - 1;
        this.form.controls['numberOfCharacters'].setValue(numberOfCharacters);
    }

    async getProductList() {
        const productResponse = await firstValueFrom(this.campaignsProductAdminApi.getProducts(this.allowedValidationTools, FilterValidationToolOperatorEnum.Contains));
        if (productResponse.length > 0) {
            for (let index = 0; index < productResponse.length; index++) {
                const matchSkus = productResponse[index].skus?.filter(s => s.consumptionValidationTools
                    && s.consumptionValidationTools.some(v => v.validationToolEnum && this.allowedValidationTools.includes(ConsumptionValidationToolEnum[v.validationToolEnum])));
                if (matchSkus && matchSkus.length > 0) {
                    this.productSKUList = this.productSKUList.concat(matchSkus);
                }
            }
        }
    }

    async importFromCsv(event) {
        let files = event.target.files;
        if (files.length === 0) {
            return;
        }

        this.form.patchValue({
            fileToUpload: <File>files[0]
        });
    }

    onClose(): void {
        this.dialogRef.close(undefined);
    }
}
